import React, { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, Package } from 'lucide-react';
import {
  getUserOrders,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  formatCashPrice,
  type CommerceOrder,
} from '@rehub/commerce';
import { supabase } from '../services/supabase';

interface CommerceOrdersListProps {
  userId: string;
}

export default function CommerceOrdersList({ userId }: CommerceOrdersListProps) {
  const [orders, setOrders] = useState<CommerceOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'active' | 'all'>('active');

  useEffect(() => {
    getUserOrders(supabase, userId).then(setOrders).finally(() => setLoading(false));
  }, [userId]);

  const activeStatuses = new Set(['awaiting_payment', 'awaiting_files', 'submitted', 'in_progress', 'review']);
  const displayed =
    filter === 'active' ? orders.filter((o) => activeStatuses.has(o.status)) : orders;

  return (
    <div className="min-h-screen bg-black text-slate-200">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <a href="/" className="text-slate-400 hover:text-white flex items-center gap-1 text-sm">
            <ArrowLeft size={16} /> Back to app
          </a>
        </div>
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
          <Package size={24} className="text-violet-400" />
          My Orders
        </h1>
        <p className="text-slate-400 text-sm mb-6">Done-for-you service orders</p>

        <div className="flex gap-2 mb-6">
          {(['active', 'all'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                filter === f ? 'bg-violet-600 text-white' : 'bg-slate-800 text-slate-400'
              }`}
            >
              {f === 'active' ? 'Active' : 'All orders'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-violet-400" size={32} />
          </div>
        ) : displayed.length === 0 ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-8 text-center text-slate-500">
            No orders yet.{' '}
            <a href="/services" className="text-violet-400 hover:underline">
              Order done-for-you services
            </a>
          </div>
        ) : (
          <ul className="space-y-3">
            {displayed.map((order) => (
              <li key={order.id}>
                <a
                  href={`/orders/${order.id}`}
                  className="block rounded-xl border border-slate-800 bg-slate-900/50 p-4 hover:border-violet-500/40 transition-colors"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-white">Order {order.id.slice(0, 8)}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(order.created_at).toLocaleString()}
                        {order.payment_method ? ` · ${order.payment_method}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-300">
                        {formatCashPrice(order.cash_amount_pence, order.currency)}
                      </span>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${ORDER_STATUS_COLORS[order.status]}`}
                      >
                        {ORDER_STATUS_LABELS[order.status]}
                      </span>
                    </div>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
